import { useEffect, useState } from "react";
import { Box, Typography, useTheme, Menu, MenuItem } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import Assets from "../../Component/Common/Assets";
import { useDispatch } from "react-redux";
import useSnackBar from "../../Redux/hooks/useSnackBar";
import usePageLoader from "../../Redux/hooks/usePageLoader";
import { getSalesAndPurchaseAction } from "../../Redux/Actions/DashboardActions";
import ErrorHandler from "../../lib/errorHandler";
import dayjs from "dayjs";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const SalesPurchaseChart = () => {
  const dispatch = useDispatch();
  const { setSnackBar } = useSnackBar();
  const setFullPageLoader = usePageLoader();
  const theme = useTheme();

  // --------------------- State ---------------------
  const [yearMenuAnchor, setYearMenuAnchor] = useState(null);

  const [chartData, setChartData] = useState({
    labels: [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ],
    datasets: [
      {
        label: "Purchase",
        data: new Array(12).fill(0),
        backgroundColor: theme.palette.lightTomato.main,
        borderRadius: 40,
        borderWidth: 2,
        barThickness: 14,
        borderColor: theme.palette.bgWhite.third,
      },
      {
        label: "Sales",
        data: new Array(12).fill(0),
        backgroundColor: theme.palette.lightYellow.secondary,
        borderRadius: 40,
        borderWidth: 2,
        barThickness: 14,
        borderColor: theme.palette.bgWhite.third,
      },
    ],
  });

  const [valueScale, setValueScale] = useState(() => getValueScale(chartData));

  const currentDate = dayjs();
  const financialYears = generateFinancialYears(10);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(financialYears[0]);

  // --------------------- Helpers ---------------------
  function getValueScale(data) {
    const allValues = [...data.datasets[0].data, ...data.datasets[1].data];
    const maxValue = Math.max(...allValues.map((val) => val || 0));

    if (maxValue >= 10000000) return { divisor: 10000000, suffix: "Cr", stepSize: 5000000 };
    if (maxValue >= 100000) return { divisor: 100000, suffix: "L", stepSize: 100000 };
    if (maxValue >= 1000) return { divisor: 1000, suffix: "K", stepSize: 10000 };
    return { divisor: 1, suffix: "", stepSize: 100 };
  }

  function generateFinancialYears(count) {
    const currentYear = currentDate.year();
    const currentMonth = currentDate.month();
    const financialYearStartMonth = 3; // April
    const currentFinancialYearStart =
      currentMonth >= financialYearStartMonth ? currentYear : currentYear - 1;

    return Array.from({ length: count }, (_, i) => {
      const startYear = currentFinancialYearStart - i;
      return `${startYear}-${(startYear + 1).toString().slice(-2)}`;
    });
  }

  function getFinancialYearRange() {
    const startYear = parseInt(selectedFinancialYear.split("-")[0]);
    const endYear = startYear + 1;

    const startDate = dayjs().year(startYear).month(3).date(1).format("YYYY-MM-DD"); // Apr 1
    const endDate = dayjs().year(endYear).month(2).date(31).format("YYYY-MM-DD");   // Mar 31

    return { startDate, endDate };
  }

  function processApiData(apiData) {
    if (!Array.isArray(apiData) || apiData.length === 0) return;

    const salesData = new Array(12).fill(0);
    const purchaseData = new Array(12).fill(0);

    const monthIndices = {
      January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
      July: 6, August: 7, September: 8, October: 9, November: 10, December: 11,
    };

    apiData.forEach((item) => {
      const monthIndex = monthIndices[item.month];
      if (monthIndex !== undefined) {
        salesData[monthIndex] = item.sales || 0;
        purchaseData[monthIndex] = item.purchase || 0;
      }
    });

    setChartData((prevData) => ({
      ...prevData,
      datasets: [
        { ...prevData.datasets[0], data: purchaseData },
        { ...prevData.datasets[1], data: salesData },
      ],
    }));
  }

  function calculateYAxisMax() {
    const allValues = [...chartData.datasets[0].data, ...chartData.datasets[1].data];
    const maxValue = Math.max(...allValues);

    if (maxValue === 0) return 100;

    const paddedMax = maxValue * 1.2;
    if (paddedMax >= 10000000) return Math.ceil(paddedMax / 5000000) * 5000000;
    if (paddedMax >= 100000) return Math.ceil(paddedMax / 100000) * 100000;
    if (paddedMax >= 1000) return Math.ceil(paddedMax / 10000) * 10000;
    return Math.ceil(paddedMax / 100) * 100;
  }

  // --------------------- Handlers ---------------------
  const handleYearClick = (event) => {
    setYearMenuAnchor(event.currentTarget);
  };

  const handleFinancialYearSelect = (financialYear) => {
    setSelectedFinancialYear(financialYear);
    setYearMenuAnchor(null);
  };

const getSalesData = async () => {
  setFullPageLoader(true);
  const { startDate, endDate } = getFinancialYearRange();
  const payload = { startDate, endDate };

  try {
    const res = await dispatch(getSalesAndPurchaseAction(payload));
    const ok = ErrorHandler(res, setSnackBar);

    if (ok && res.payload?.data?.response) {
      processApiData(res.payload.data.response.combinedMonthly);
    }
  } catch (err) {
    setSnackBar("error", err.message);
  } finally {
    setFullPageLoader(false);
  }
};

  // --------------------- Effects ---------------------
  useEffect(() => {
    getSalesData();
  }, [selectedFinancialYear]);

  useEffect(() => {
    setValueScale(getValueScale(chartData));
  }, [chartData]);

  // --------------------- Chart Options ---------------------
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.raw;
            const label = context.dataset.label;
            if (value >= 10000000) return `${label}: ₹${(value / 10000000).toFixed(2)} Cr`;
            if (value >= 100000) return `${label}: ₹${(value / 100000).toFixed(2)} L`;
            if (value >= 1000) return `${label}: ₹${(value / 1000).toFixed(2)} K`;
            return `${label}: ₹${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: theme.palette.bgBlack.secondary,
          font: { size: 14, weight: 500 },
        },
        barPercentage: 0.4,
        categoryPercentage: 0.3,
      },
      y: {
        beginAtZero: true,
        max: calculateYAxisMax(),
        grid: { color: "#f5f5f5", drawBorder: false },
        border: { display: false },
        ticks: {
          stepSize: valueScale.stepSize,
          color: theme.palette.bgBlack.secondary,
          font: { size: 14, weight: 500 },
          callback: (value) => `${(value / valueScale.divisor).toFixed(1)}${valueScale.suffix}`,
        },
      },
    },
    layout: { padding: { top: 20, right: 20, bottom: 10, left: 10 } },
    interaction: { intersect: false, mode: "index" },
    elements: { bar: { spacing: 10 } },
  };

  // --------------------- Render ---------------------
  return (
    <Box
      sx={{
        width: "100%",
        height: "352px",
        background: theme.palette.bgWhite.third,
        borderRadius: "20px",
        border: "1px solid",
        borderColor: theme.palette.bgWhite.secondary,
        padding: "20px 20px",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          mb: 3,
        }}
      >
        <Typography fontWeight={700} fontSize="18px" color={theme.palette.bgBlack.secondary}>
          Sales & Purchase
        </Typography>

        {/* Legends */}
        <Box sx={{ display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Box sx={{ borderRadius: "50%", background: theme.palette.lightTomato.main, width: 15, height: 15 }} />
            <Typography fontWeight={500} fontSize="12px">Purchase</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Box sx={{ borderRadius: "50%", background: theme.palette.lightYellow.secondary, width: 15, height: 15 }} />
            <Typography fontWeight={500} fontSize="12px">Sales</Typography>
          </Box>
        </Box>

        {/* Year Dropdown */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "14px",
          }}
        >
          <Box
            onClick={handleYearClick}
            sx={{
              display: "flex",
              padding: "6px 16px",
              alignItems: "center",
              gap: "8px",
              borderRadius: "30px",
              border: `1px solid ${theme.palette.bgWhite.secondary}`,
              boxShadow:
                "46px 32px 16px 0 rgba(183, 194, 215, 0.00), 29px 21px 14px 0 rgba(183, 194, 215, 0.01), 16px 12px 12px 0 rgba(183, 194, 215, 0.05), 7px 5px 9px 0 rgba(183, 194, 215, 0.09), 2px 1px 5px 0 rgba(183, 194, 215, 0.10)",
              cursor: "pointer",
            }}
          >
            <Assets absolutePath src="/assets/img/Calendar.svg" />
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: theme.palette.bgDarkGray.grey600 }}>
              FY {selectedFinancialYear}
            </Typography>
          </Box>
          <Menu anchorEl={yearMenuAnchor} open={Boolean(yearMenuAnchor)} onClose={() => setYearMenuAnchor(null)}>
            {financialYears.map((year) => (
              <MenuItem key={year} onClick={() => handleFinancialYearSelect(year)}>
                FY {year}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ height: "240px", width: "100%" }}>
        <Bar data={chartData} options={options} />
      </Box>
    </Box>
  );
};

export default SalesPurchaseChart;
